import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Autocomplete, TextField, Chip } from "@mui/material";
import { ValidAny } from "@/types";

interface MultiSelectFilterProps {
  source: string;
  label: string;
  choices: { id: string; name: string }[];
}

/**
 * Compact multi-select filter that syncs selections to URL params.
 * Uses small sizing to fit the dashboard filter bar without dominating the layout.
 */
export const MultiSelectFilter = ({ source, label, choices }: MultiSelectFilterProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Parse current filters from URL
  let currentFilters: Record<string, string[]> = {};
  try {
    const filterParam = searchParams.get("filter");
    currentFilters = filterParam ? JSON.parse(filterParam) : {};
  } catch {
    currentFilters = {};
  }

  const currentValue = currentFilters[source] ?? [];
  const selectedChoices = choices.filter((choice) => currentValue.includes(choice.id));

  const handleChange = useCallback(
    (_event: ValidAny, newValue: { id: string; name: string }[]) => {
      const params = new URLSearchParams(searchParams);
      const newFilters = { ...currentFilters };
      const ids = newValue.map((v) => v.id);

      if (ids.length > 0) {
        newFilters[source] = ids;
      } else {
        delete newFilters[source];
      }

      if (Object.keys(newFilters).length === 0) {
        params.delete("filter");
      } else {
        params.set("filter", JSON.stringify(newFilters));
      }

      void navigate({ search: params.toString() }, { replace: true });
    },
    [searchParams, currentFilters, source, navigate]
  );

  return (
    <Autocomplete
      multiple
      size="small"
      options={choices}
      value={selectedChoices}
      onChange={handleChange}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      slotProps={{
        listbox: { sx: { fontSize: "0.8rem" } },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selectedChoices.length === 0 ? "All" : ""}
          variant="outlined"
          size="small"
          sx={{
            "& .MuiInputBase-root": { fontSize: "0.8rem", py: 0 },
            "& .MuiInputLabel-root": { fontSize: "0.8rem" },
          }}
        />
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.name}
            size="small"
            sx={{ fontSize: "0.7rem", height: 22 }}
          />
        ))
      }
    />
  );
};
